import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Loader2, Gift, CalendarX } from 'lucide-react';

// Öffentliche Empfehlungs-Landing-Page (per WhatsApp/QR geteilt).
// Mobil-first, gleiche Optik wie Beratungs-/Stammplatz-Seite.
const P1_RED = '#d1272e';
const API = process.env.REACT_APP_BACKEND_URL;

export default function EmpfehlungPage() {
  const { code } = useParams();
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [honeypot, setHoneypot] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/public/empfehlung/${code}`);
      if (!res.ok) throw new Error();
      setInfo(await res.json());
    } catch {
      setInvalid(true);
    }
    setLoading(false);
  }, [code]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setError('');
    if (form.name.trim().length < 3 || !form.name.trim().includes(' ')) {
      setError('Bitte gib deinen Vor- und Nachnamen an.'); return;
    }
    if (form.phone.replace(/\D/g, '').length < 8) {
      setError('Bitte gib deine Handynummer an.'); return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/public/empfehlung/${code}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, website: honeypot }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.detail || 'Das hat leider nicht geklappt.');
      setDone(d.message || 'Das Team meldet sich bei dir!');
    } catch (e) {
      setError(e.message);
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="empfehlung-page">
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center justify-center">
        <img src="/logo-black.png" alt="P1 Performance Center" className="h-9 object-contain" />
      </div>

      <div className="max-w-md mx-auto px-4 py-6 pb-16">
        {loading ? (
          <div className="text-center py-24">
            <Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: P1_RED }} />
          </div>
        ) : invalid ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center mt-8">
            <CalendarX className="w-10 h-10 mx-auto text-gray-300 mb-3" />
            <h1 className="text-lg font-bold">Link nicht gültig</h1>
            <p className="text-sm text-gray-500 mt-2">Frag dein P1-Mitglied nach einem neuen Empfehlungslink.</p>
          </div>
        ) : done ? (
          <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center mt-8" data-testid="empfehlung-done">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3" style={{ color: '#16a34a' }} />
            <h1 className="text-xl font-extrabold">Das war's schon! 🎉</h1>
            <p className="text-sm text-gray-600 mt-3">{done}</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <h1 className="text-2xl font-extrabold tracking-tight">
                {info.vorname} lädt dich zu P1 ein 🥊
              </h1>
              <p className="text-sm text-gray-500 mt-2">
                Kampfsport & Fitness in Eutingen — komm zum Probetraining.
              </p>
              <div className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-full text-sm font-bold text-white" style={{ backgroundColor: P1_RED }}>
                <Gift className="w-4 h-4" />
                {info.punkte_neu} Bonus-Punkte geschenkt, wenn du Mitglied wirst
              </div>
            </div>

            {error && <div className="mb-4 text-sm text-white rounded-xl px-4 py-3" style={{ backgroundColor: P1_RED }}>{error}</div>}

            <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-4">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">Vor- und Nachname</label>
                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Max Mustermann"
                  className="mt-1 w-full border border-gray-300 rounded-xl px-4 py-3 text-base" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">Handynummer</label>
                <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="0176 12345678" type="tel"
                  className="mt-1 w-full border border-gray-300 rounded-xl px-4 py-3 text-base" />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase">E-Mail (optional)</label>
                <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="deine@email.de" type="email"
                  className="mt-1 w-full border border-gray-300 rounded-xl px-4 py-3 text-base" />
              </div>
              {/* Honeypot — unsichtbar für Menschen */}
              <input value={honeypot} onChange={e => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off"
                style={{ position: 'absolute', left: '-9999px', height: 0, width: 0, opacity: 0 }} aria-hidden="true" />

              <button onClick={submit} disabled={submitting}
                className="w-full text-white font-bold rounded-xl py-3.5 text-base disabled:opacity-60"
                style={{ backgroundColor: P1_RED }} data-testid="empfehlung-submit">
                {submitting ? 'Wird gesendet…' : 'Probetraining anfragen'}
              </button>
              <p className="text-[11px] text-gray-400 text-center">
                Wir nutzen deine Daten nur, um dich für dein Probetraining zu kontaktieren.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
